/**
 * RE:LOCATE （実験的機能）。
 *
 * Nuendo のプレビュー用トラックアーカイブ XML（ビデオトラック + マーカー
 * トラック）の内容を、同じフォルダにある REAPER プロジェクト（.RPP）へ移植した
 * 新しい RPP をダウンロードする。処理はすべてブラウザ内で行い、ファイルは
 * どこにもアップロードしない。
 *
 * 前提の構成: RPP と XML が同じ階層にあり、動画・音声はその下の Media/ に置く。
 * 移植ルールは utils/migrationPlan.ts、RPP への差し込みは utils/rppWriter.ts。
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Button from "../../../components/button/Button";
import DropZoneOverlay from "../../../components/drop-zone-overlay/DropZoneOverlay";
import Select from "../../../components/form-item/Select";
import HelpHint from "../../../components/help-hint/HelpHint";
import StatusBadge from "../../../components/status-badge/StatusBadge";
import Table, { type TableColumn } from "../../../components/table/Table";
import type { FileWithPath } from "../../../hooks/useS3Upload";
import { triggerBlobDownload } from "../../../utils/downloadFile";
import logoImage from "../../../assets/images/card_tool_re_locate.webp";
import { readAudioDuration } from "./utils/audioDuration";
import {
  buildMigrationPlan,
  type MigrationPlan,
  type PlannedItem,
} from "./utils/migrationPlan";
import { parseNuendoArchive } from "./utils/nuendoXml";
import {
  classifyProjectFiles,
  filesFromDirectoryInput,
  RELOCATED_RPP_SUFFIX,
  type ProjectFolder,
} from "./utils/projectFolder";
import { mergeIntoRpp } from "./utils/rppWriter";

const HELP_DESCRIPTION = [
  "Nuendo のプレビュー用 XML（トラックアーカイブ）の内容を、REAPER のプロジェクト（.RPP）に移植するツールです（実験的機能）。",
  "RPP・XML・Media フォルダが入ったプロジェクトフォルダをドロップしてください。RPP と XML は同じ階層、動画と音声は Media に置く前提です。",
  "ビデオトラック: 動画をそのまま配置します。動画の音声は鳴らしません（映像は表示されます）。",
  "マーカートラック: サイクルマーカーはリージョン、マーカーはマーカーとして、マーカートラック名と同じ名前のルーラーレーンに移植します。",
  "サイクルマーカーと同名の音声（wav / aif / flac / mp3 / ogg）が Media にあれば、マーカートラック名のトラックに配置し、そのリージョンのレンダー対象トラックにします。",
  "元の RPP は書き換えません。「元の名前_relocate.RPP」としてダウンロードされるので、元の RPP と同じフォルダに置いて開いてください。",
].join("\n");

const HeaderLogo = styled.img`
  width: min(420px, 100%);
  object-fit: contain;
  align-self: flex-start;
`;

/** フォルダの受け口。ドラッグ中の表示は DropZoneOverlay が重ねる */
const DropArea = styled.div`
  width: 100%;
  min-height: 160px;
  border: 2px dashed var(--soremo-border);
  border-radius: 8px;
  background: #fff;
`;

const DropIcon = styled.span.attrs({ className: "material-symbols-rounded" })`
  font-size: 40px;
  color: var(--soremo-light-gray);
`;

const SelectSlot = styled.div`
  width: min(360px, 100%);
`;

const columns: TableColumn[] = [
  { key: "kind", header: "種別", width: 96, minWidth: 80 },
  { key: "place", header: "トラック / レーン", width: 140, minWidth: 100 },
  { key: "name", header: "名前", minWidth: 160 },
  { key: "start", header: "位置", width: 104, minWidth: 88 },
  { key: "length", header: "長さ", width: 104, minWidth: 88 },
  { key: "file", header: "参照ファイル", minWidth: 200 },
  { key: "status", header: "状態", width: 132, minWidth: 104 },
];

interface PreviewRow {
  key: string;
  kind: "動画" | "リージョン" | "マーカー" | "オーディオ";
  place: string;
  name: string;
  start: number;
  length: number | null;
  item: PlannedItem | null;
}

/** 秒 → `m:ss.mmm` */
const formatSeconds = (value: number): string => {
  const totalMs = Math.round(Math.max(0, value) * 1000);
  const minutes = Math.floor(totalMs / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
};

/** `foo.RPP` → `foo_relocate.RPP`（拡張子の大文字小文字は元のまま） */
const outputFileName = (rppName: string): string => {
  const dot = rppName.lastIndexOf(".");
  return dot > 0
    ? `${rppName.slice(0, dot)}${RELOCATED_RPP_SUFFIX}${rppName.slice(dot)}`
    : `${rppName}${RELOCATED_RPP_SUFFIX}.RPP`;
};

const buildPreviewRows = (plan: MigrationPlan): PreviewRow[] => {
  const rows: PreviewRow[] = [];
  plan.tracks
    .filter((track) => track.kind === "video")
    .forEach((track) =>
      track.items.forEach((item, index) =>
        rows.push({
          key: `${track.key}-${index}`,
          kind: "動画",
          place: track.name,
          name: item.name,
          start: item.position,
          length: item.length,
          item,
        }),
      ),
    );
  plan.markers.forEach((marker, index) => {
    rows.push({
      key: `marker-${index}`,
      kind: marker.end === null ? "マーカー" : "リージョン",
      place: marker.laneName,
      name: marker.name,
      start: marker.start,
      length: marker.end === null ? null : marker.end - marker.start,
      item: null,
    });
    if (marker.audioItem) {
      rows.push({
        key: `marker-${index}-audio`,
        kind: "オーディオ",
        place: marker.laneName,
        name: marker.audioItem.name,
        start: marker.audioItem.position,
        length: marker.audioItem.length,
        item: marker.audioItem,
      });
    }
  });
  return rows;
};

const StatusCell: React.FC<{ item: PlannedItem | null }> = ({ item }) => {
  if (!item) return <span className="bodytext-13 u-text-light-gray">-</span>;
  if (!item.found) return <StatusBadge label="Media に無し" color="green" />;
  if (item.sourceType !== "VIDEO" && item.lengthSource === "timeline") {
    return <StatusBadge label="マーカー長で配置" color="light-gray" />;
  }
  return <StatusBadge label="OK" color="light-gray" />;
};

const ReLocate: React.FC = () => {
  const [folder, setFolder] = useState<ProjectFolder | null>(null);
  const [rppName, setRppName] = useState("");
  const [xmlName, setXmlName] = useState("");
  const [plan, setPlan] = useState<MigrationPlan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [isPlanning, setIsPlanning] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const directoryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // React の型定義に webkitdirectory が無いため属性で付ける
    directoryInputRef.current?.setAttribute("webkitdirectory", "");
  }, []);

  const loadFiles = useCallback((files: FileWithPath[]) => {
    const next = classifyProjectFiles(files);
    if (next.rppFiles.length === 0) {
      toast.error(
        "RPP ファイルが見つかりません。RPP・XML・Media が入ったプロジェクトフォルダを読み込んでください",
      );
      return;
    }
    if (next.xmlFiles.length === 0) {
      toast.error("RPP と同じ階層に XML ファイルが見つかりません");
      return;
    }
    setFolder(next);
    setRppName(next.rppFiles[0].name);
    setXmlName(next.xmlFiles[0].name);
  }, []);

  const handlePlainDrop = useCallback(
    (files: File[]) =>
      loadFiles(files.map((file) => ({ file, relativePath: file.name }))),
    [loadFiles],
  );

  const handleDirectoryInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      loadFiles(filesFromDirectoryInput(event.target.files));
    }
    // 同じフォルダを選び直しても change が発火するよう空にする
    event.target.value = "";
  };

  const xmlFile = useMemo(
    () => folder?.xmlFiles.find((file) => file.name === xmlName) ?? null,
    [folder, xmlName],
  );
  const rppFile = useMemo(
    () => folder?.rppFiles.find((file) => file.name === rppName) ?? null,
    [folder, rppName],
  );

  useEffect(() => {
    if (!folder || !xmlFile) {
      setPlan(null);
      setPlanError(null);
      return;
    }
    let cancelled = false;
    setIsPlanning(true);
    (async () => {
      try {
        const archive = parseNuendoArchive(await xmlFile.text());
        const next = await buildMigrationPlan(
          archive,
          folder.media,
          (entry) => readAudioDuration(entry.file),
          folder.mediaDirName,
        );
        if (cancelled) return;
        setPlan(next);
        setPlanError(null);
      } catch (error) {
        if (cancelled) return;
        setPlan(null);
        setPlanError(
          error instanceof Error
            ? error.message
            : "XML の読み込みに失敗しました",
        );
      } finally {
        if (!cancelled) setIsPlanning(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [folder, xmlFile]);

  const rows = useMemo(() => (plan ? buildPreviewRows(plan) : []), [plan]);

  const summary = useMemo(() => {
    if (!plan) return null;
    const regions = plan.markers.filter((m) => m.end !== null);
    const videos = plan.tracks
      .filter((t) => t.kind === "video")
      .reduce((sum, t) => sum + t.items.length, 0);
    return [
      `動画 ${videos} 件`,
      `リージョン ${regions.length} 件（うち音声を配置 ${regions.filter((m) => m.audioItem).length} 件）`,
      `マーカー ${plan.markers.length - regions.length} 件`,
      `ルーラーレーン ${plan.lanes.length} 本`,
    ].join(" / ");
  }, [plan]);

  const handleDownload = async () => {
    if (!plan || !rppFile) return;
    setIsExporting(true);
    try {
      const merged = mergeIntoRpp(await rppFile.text(), plan);
      const fileName = outputFileName(rppFile.name);
      triggerBlobDownload(
        new Blob([merged], { type: "application/octet-stream" }),
        fileName,
      );
      toast.success(
        `${fileName} をダウンロードしました。元の RPP と同じフォルダに置いて開いてください`,
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "RPP の書き出しに失敗しました",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const hasMigrationContent =
    !!plan && plan.tracks.length + plan.markers.length > 0;

  return (
    <div className="u-col-center u-gap24 u-pt32 u-pb32">
      <div className="u-wrapper u-col u-gap8">
        <HeaderLogo src={logoImage} alt="RE:LOCATE" />
        <div className="u-row u-gap8 u-items-center">
          <h1 className="heading-24">RE:LOCATE</h1>
          <HelpHint description={HELP_DESCRIPTION} placement="bottom" />
        </div>
        <p className="bodytext-13 u-text-gray">
          Nuendo のプレビュー用 XML を、REAPER
          のプロジェクトへお引越し。動画・マーカー・サイクルマーカーの音を RPP
          にまとめて移します。
        </p>
      </div>

      <div className="u-wrapper u-col u-gap16">
        <input
          ref={directoryInputRef}
          type="file"
          multiple
          hidden
          onChange={handleDirectoryInput}
        />
        <DropZoneOverlay
          enableFolderDrop
          onDrop={handlePlainDrop}
          onDropWithPath={loadFiles}
          overlayText="プロジェクトフォルダをドロップ"
        >
          <DropArea className="u-col-center u-justify-center u-gap12 u-ptb16">
            <DropIcon>
              {folder ? "folder_open" : "drive_folder_upload"}
            </DropIcon>
            {folder ? (
              <span className="bodytext-13">
                {folder.baseDir || "（ドロップしたファイル）"}
                {" / "}
                {folder.mediaDirName
                  ? `${folder.mediaDirName} 内のファイル ${folder.media.size} 件`
                  : "Media フォルダなし"}
              </span>
            ) : (
              <span className="bodytext-13 u-text-gray u-text-center">
                RPP・XML・Media フォルダが入ったプロジェクトフォルダをドロップ
              </span>
            )}
            <Button
              size="small"
              variant="outlined"
              leftIcon="folder"
              onClick={() => directoryInputRef.current?.click()}
            >
              {folder ? "別のフォルダを選ぶ" : "フォルダを選ぶ"}
            </Button>
          </DropArea>
        </DropZoneOverlay>

        {folder && (
          <div className="u-row u-gap16 u-wrap u-w100">
            <SelectSlot>
              <Select
                label="移植先の RPP"
                name="re-locate-rpp"
                value={rppName}
                onChange={(e) => setRppName(e.target.value)}
                options={folder.rppFiles.map((file) => ({
                  value: file.name,
                  name: file.name,
                }))}
              />
            </SelectSlot>
            <SelectSlot>
              <Select
                label="Nuendo の XML"
                name="re-locate-xml"
                value={xmlName}
                onChange={(e) => setXmlName(e.target.value)}
                options={folder.xmlFiles.map((file) => ({
                  value: file.name,
                  name: file.name,
                }))}
              />
            </SelectSlot>
          </div>
        )}

        {folder && !folder.mediaDirName && (
          <p className="bodytext-13 u-text-green">
            Media フォルダが見つかりません。動画は Media/
            を参照して書き出し、音声は配置しません。
          </p>
        )}
        {planError && <p className="bodytext-13 u-text-green">{planError}</p>}
        {isPlanning && (
          <p className="bodytext-13 u-text-gray">
            XML と Media を確認しています…
          </p>
        )}

        {plan && !isPlanning && (
          <>
            <div className="u-row-between u-gap16 u-wrap u-items-center u-w100">
              <span className="bodytext-13">{summary}</span>
              <Button
                leftIcon="download"
                disabled={!rppFile || !hasMigrationContent || isExporting}
                onClick={handleDownload}
              >
                RPP をダウンロード
              </Button>
            </div>
            {plan.warnings.length > 0 && (
              <ul className="u-col u-gap4 u-m0">
                {plan.warnings.map((warning) => (
                  <li key={warning} className="bodytext-13 u-text-green">
                    {warning}
                  </li>
                ))}
              </ul>
            )}
            <Table columns={columns} minWidth={960} maxBodyHeight={560}>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className="bodytext-13">{row.kind}</td>
                  <td className="bodytext-13">{row.place}</td>
                  <td className="bodytext-13">{row.name || "（名前なし）"}</td>
                  <td className="bodytext-13">{formatSeconds(row.start)}</td>
                  <td className="bodytext-13">
                    {row.length === null ? "-" : formatSeconds(row.length)}
                  </td>
                  <td className="bodytext-13 u-text-gray">
                    {row.item?.filePath ?? "-"}
                  </td>
                  <td>
                    <StatusCell item={row.item} />
                  </td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </div>
    </div>
  );
};

export default ReLocate;
